/**
 * Gielle Search Basic v1
 * Filtri trasversali: Categoria + Prezzo. Mobile drawer + category chips.
 */
class GielleSearchBasic {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('[ref="grid"]');
    this.sidebar = root.querySelector('[data-filter-sidebar]');
    this.backdrop = root.querySelector('[data-mobile-filter-backdrop]');
    this.categoryOptions = root.querySelector('[data-options-category]');
    this.chips = root.querySelector('[data-category-chips]');
    this.priceMin = root.querySelector('[data-price-min]');
    this.priceMax = root.querySelector('[data-price-max]');
    this.priceHint = root.querySelector('[data-price-hint]');
    this.activeSummary = root.querySelector('[data-active-filters]');
    this.clearButtons = root.querySelectorAll('[data-clear-filters], [data-clear-filters-mobile]');
    this.sortSelects = root.querySelectorAll('[data-gielle-sort]');
    this.noResults = root.querySelector('[data-no-filter-results]');
    this.state = { categories: new Set(), min: null, max: null };
    if (!this.grid) return;
    this.readUrl();
    this.renderFilters();
    this.syncPriceInputs();
    this.bind();
    this.apply();
  }

  get cards() {
    return Array.from(this.grid.querySelectorAll(':scope > [data-gielle-search-card]'));
  }

  readUrl() {
    const params = new URLSearchParams(location.search);
    const categories = params.get('gf_category');
    if (categories) this.state.categories = new Set(categories.split('|').filter(Boolean));
    const min = parseFloat(params.get('gf_min'));
    const max = parseFloat(params.get('gf_max'));
    this.state.min = Number.isFinite(min) ? min : null;
    this.state.max = Number.isFinite(max) ? max : null;
  }

  writeUrl() {
    const url = new URL(location.href);
    const categories = Array.from(this.state.categories);
    if (categories.length) url.searchParams.set('gf_category', categories.join('|'));
    else url.searchParams.delete('gf_category');
    if (Number.isFinite(this.state.min)) url.searchParams.set('gf_min', String(this.state.min));
    else url.searchParams.delete('gf_min');
    if (Number.isFinite(this.state.max)) url.searchParams.set('gf_max', String(this.state.max));
    else url.searchParams.delete('gf_max');
    history.replaceState(history.state, '', url.toString());
  }

  categories() {
    const counts = new Map();
    for (const card of this.cards) {
      const category = card.dataset.category || 'Altri prodotti';
      counts.set(category, (counts.get(category) || 0) + 1);
    }
    const order = ['Pavimenti e rivestimenti', 'Outdoor', 'Arredo bagno', 'Sanitari', 'Rubinetteria', 'Accessori', 'Outlet', 'Altri prodotti'];
    return Array.from(counts.entries()).sort((a, b) => {
      const ai = order.indexOf(a[0]);
      const bi = order.indexOf(b[0]);
      if (ai !== -1 || bi !== -1) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      return a[0].localeCompare(b[0], 'it');
    });
  }

  renderFilters() {
    const categories = this.categories();
    if (this.categoryOptions) {
      this.categoryOptions.innerHTML = categories.map(([label, count]) => {
        const checked = this.state.categories.has(label) ? 'checked' : '';
        return `<label class="gielle-basic-filter-option">
          <input type="checkbox" data-category-filter value="${this.escape(label)}" ${checked}>
          <span class="gielle-basic-filter-option__box" aria-hidden="true"></span>
          <span>${this.escape(label)}</span>
          <span class="gielle-basic-filter-option__count">${count}</span>
        </label>`;
      }).join('');
    }

    if (this.chips) {
      const total = this.cards.length;
      const activeAll = this.state.categories.size === 0 ? ' is-active' : '';
      this.chips.innerHTML = `<button type="button" class="gielle-search-basic__chip${activeAll}" data-category-chip="">Tutti (${total})</button>` +
        categories.map(([label, count]) => {
          const active = this.state.categories.size === 1 && this.state.categories.has(label) ? ' is-active' : '';
          return `<button type="button" class="gielle-search-basic__chip${active}" data-category-chip="${this.escape(label)}">${this.escape(label)} (${count})</button>`;
        }).join('');
    }

    const prices = this.cards.map(card => parseFloat(card.dataset.price || '')).filter(Number.isFinite);
    if (prices.length && this.priceHint) {
      const low = Math.floor(Math.min(...prices));
      const high = Math.ceil(Math.max(...prices));
      this.priceHint.textContent = `Intervallo dei risultati: € ${low} – € ${high}`;
      if (this.priceMin) this.priceMin.placeholder = String(low);
      if (this.priceMax) this.priceMax.placeholder = String(high);
    }
  }

  syncPriceInputs() {
    if (this.priceMin && Number.isFinite(this.state.min)) this.priceMin.value = String(this.state.min);
    if (this.priceMax && Number.isFinite(this.state.max)) this.priceMax.value = String(this.state.max);
  }

  bind() {
    this.root.addEventListener('change', (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.matches('[data-category-filter]')) {
        if (target.checked) this.state.categories.add(target.value);
        else this.state.categories.delete(target.value);
        this.writeUrl();
        this.renderFilters();
        this.apply();
      }
    });

    this.root.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-category-chip]');
      if (chip) {
        const value = chip.dataset.categoryChip || '';
        this.state.categories.clear();
        if (value) this.state.categories.add(value);
        this.writeUrl();
        this.renderFilters();
        this.apply();
        return;
      }
      if (event.target.closest('[data-mobile-filter-open]')) this.openDrawer();
      if (event.target.closest('[data-mobile-filter-close], [data-mobile-filter-show]')) this.closeDrawer();
      if (event.target.closest('[data-mobile-filter-backdrop]')) this.closeDrawer();
    });

    const priceChanged = () => {
      const min = parseFloat(this.priceMin?.value || '');
      const max = parseFloat(this.priceMax?.value || '');
      this.state.min = Number.isFinite(min) ? min : null;
      this.state.max = Number.isFinite(max) ? max : null;
      this.writeUrl();
      this.apply();
    };
    this.priceMin?.addEventListener('input', priceChanged);
    this.priceMax?.addEventListener('input', priceChanged);

    for (const button of this.clearButtons) button.addEventListener('click', () => this.clear());

    for (const select of this.sortSelects) {
      select.addEventListener('change', () => {
        const url = new URL(location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        location.assign(url.toString());
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawer();
    });
  }

  clear() {
    this.state.categories.clear();
    this.state.min = null;
    this.state.max = null;
    if (this.priceMin) this.priceMin.value = '';
    if (this.priceMax) this.priceMax.value = '';
    this.writeUrl();
    this.renderFilters();
    this.apply();
  }

  apply() {
    let visible = 0;
    for (const card of this.cards) {
      const category = card.dataset.category || 'Altri prodotti';
      const price = parseFloat(card.dataset.price || '');
      const categoryOk = this.state.categories.size === 0 || this.state.categories.has(category);
      const minOk = !Number.isFinite(this.state.min) || (Number.isFinite(price) && price >= this.state.min);
      const maxOk = !Number.isFinite(this.state.max) || (Number.isFinite(price) && price <= this.state.max);
      const match = categoryOk && minOk && maxOk;
      card.hidden = !match;
      if (match) visible += 1;
    }
    if (this.noResults) this.noResults.hidden = visible !== 0;
    this.renderSummary();
  }

  renderSummary() {
    if (!this.activeSummary) return;
    const labels = [...this.state.categories];
    if (Number.isFinite(this.state.min)) labels.push(`da € ${this.state.min}`);
    if (Number.isFinite(this.state.max)) labels.push(`fino a € ${this.state.max}`);
    this.activeSummary.textContent = labels.length ? `Filtri: ${labels.join(' · ')}` : 'Nessun filtro selezionato';
    const hasFilters = labels.length > 0;
    for (const button of this.clearButtons) button.toggleAttribute('hidden', !hasFilters && button.hasAttribute('data-clear-filters'));
  }

  openDrawer() {
    if (!this.sidebar) return;
    this.sidebar.classList.add('is-open');
    this.backdrop?.classList.add('is-open');
    if (this.backdrop) this.backdrop.hidden = false;
    document.documentElement.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.sidebar?.classList.remove('is-open');
    this.backdrop?.classList.remove('is-open');
    if (this.backdrop) this.backdrop.hidden = true;
    document.documentElement.style.overflow = '';
  }

  escape(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
}

function initGielleSearchBasic() {
  document.querySelectorAll('[data-gielle-search-basic]').forEach(root => {
    if (root.dataset.gielleBasicReady === 'true') return;
    root.dataset.gielleBasicReady = 'true';
    new GielleSearchBasic(root);
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initGielleSearchBasic, { once:true });
else initGielleSearchBasic();
document.addEventListener('shopify:section:load', initGielleSearchBasic);
